import { BrandingConfig } from '../../types/branding';

/**
 * Maps branding configuration to CSS custom properties (variables)
 * Uses consistent --brand-* naming convention for theming
 */
export function brandingToCssVars(branding: BrandingConfig): Record<string, string> {
  return {
    '--brand-primary': branding.primaryColor || '#0F766E',
    '--brand-secondary': branding.secondaryColor || '#134E4A',
    '--brand-accent': branding.accentColor || '#22C55E',
    '--brand-text-on-primary': branding.textOnPrimary || '#FFFFFF',
    '--brand-radius': `${branding.buttonRadiusPx ?? 8}px`
  };
}

/**
 * Generates inline style object for SSR usage
 * Prevents FOUC (Flash of Unstyled Content) by applying theme server-side
 */
export function brandingToInlineStyle(branding: BrandingConfig): React.CSSProperties {
  const cssVars = brandingToCssVars(branding);
  return cssVars as React.CSSProperties;
}

/**
 * Generates CSS string for custom CSS injection
 * Useful for email templates or dynamic style elements
 */
export function brandingToCssString(branding: BrandingConfig): string {
  const cssVars = brandingToCssVars(branding);
  const varsString = Object.entries(cssVars)
    .map(([key, value]) => `  ${key}: ${value};`)
    .join('\n');
  
  return `:root {\n${varsString}\n}`;
}

/**
 * Legacy CSS variable names for backward compatibility
 * Maps new --brand-* variables to old --primary-color format
 */
export function brandingToLegacyCssVars(branding: BrandingConfig): Record<string, string> {
  return {
    '--primary-color': branding.primaryColor || '#0F766E',
    '--secondary-color': branding.secondaryColor || '#134E4A',
    '--primary-color-rgb': hexToRgb(branding.primaryColor || '#0F766E'),
    '--secondary-color-rgb': hexToRgb(branding.secondaryColor || '#134E4A')
  };
}

/**
 * Converts hex color to RGB string for CSS rgba() usage
 */
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  
  return [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ].join(', ');
}










